import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Toaster } from "sonner";
import BrandHeader from "../components/BrandHeader";
import CoachLogoutButton from "../components/CoachLogoutButton";
import Dots from "../components/tracker/Dots";
import ActionCard from "../components/tracker/ActionCard";
import { getTracker } from "../lib/api";
import { useVisiblePolling } from "../lib/usePolling";

function SummaryBar({ data }) {
  return (
    <p className="surface-card p-4 md:p-5 mt-6 text-sm text-brand-ink flex flex-wrap lg:flex-nowrap lg:whitespace-nowrap gap-x-3 gap-y-2 overflow-x-auto" data-testid="tracker-summary-bar">
      {data.categories.map((c, i) => (
        <span key={c.key} className="inline-flex items-center gap-2" data-testid={`tracker-count-${c.key}`}>
          {i > 0 && <span className="text-brand-ink-muted">|</span>}
          <Dots dots={c.dots} size={10} /> {c.label}: <strong>{data.counts[c.key]}</strong>
        </span>
      ))}
    </p>
  );
}

function Group({ title, dots, items, testid, empty, onChanged }) {
  return (
    <section className="mt-10" data-testid={testid}>
      <h2 className="font-serif text-2xl md:text-3xl text-brand-ink flex items-center gap-3">
        {dots && <Dots dots={dots} />} {title} <span className="text-base text-brand-ink-muted">({items.length})</span>
      </h2>
      {items.length === 0 ? (
        <p className="mt-3 text-sm text-brand-ink-muted" data-testid={`${testid}-empty`}>{empty}</p>
      ) : (
        <div className="mt-4 space-y-4">{items.map((c) => <ActionCard key={c.id} card={c} onChanged={onChanged} />)}</div>
      )}
    </section>
  );
}

export default function CoachTracker() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => getTracker().then((d) => { setData(d); setErr(""); }).catch((e) => setErr(e?.response?.status === 401 ? "Your coach session has expired." : "Couldn't refresh the Tracker.")), []);
  useVisiblePolling(load, 5000);

  return (
    <div className="min-h-screen bg-brand-base" data-testid="tracker-page">
      <Toaster richColors position="top-center" />
      <BrandHeader small right={<CoachLogoutButton />} />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <Link to="/coach" className="btn-ghost inline-flex items-center gap-2 text-sm" data-testid="tracker-back-link"><ArrowLeft size={16} /> Coach Dashboard</Link>
        <div className="label-eyebrow flex items-center gap-3 mt-6"><span className="inline-block h-px w-10 bg-brand-primary" /> Coach Dashboard</div>
        <h1 className="font-serif text-4xl md:text-5xl text-brand-ink mt-3">Weekly Check In Action Tracker</h1>
        {err && <p className="mt-3 text-sm text-red-700" data-testid="tracker-error">{err}</p>}
        {!data ? <Loader2 className="mt-8 animate-spin text-brand-primary" /> : (
          <>
            <p className="mt-3 text-brand-ink-muted" data-testid="tracker-items-to-review">{data.items_to_review} Items to Review for week of {data.cycle.label}</p>
            <SummaryBar data={data} />
            {data.categories.map((c) => (
              <Group key={c.key} title={c.label} dots={c.dots} items={c.items} testid={`tracker-live-${c.key}`} empty="Nothing here right now." onChanged={load} />
            ))}
            <Group title="In Process" items={data.in_process} testid="tracker-in-process" empty="No actions in process." onChanged={load} />
            <Group title="Completed (last 24 hours)" items={data.completed} testid="tracker-completed" empty="Nothing completed in the last 24 hours." onChanged={load} />
          </>
        )}
      </main>
    </div>
  );
}
